import i18n from "i18next";
import LanguageDetector from "i18next-browser-languagedetector";
import ICU from "i18next-icu";
import { initReactI18next } from "react-i18next";
// Legacy app-wide translations to avoid regressions (namespace: translation)
import legacyEn from "../locales/en.json";
import legacyEs from "../locales/es.json";
import legacyFr from "../locales/fr.json";
// Enhanced i18n configuration

const localeCatalog = {
  en: {
    name: "English",
    nativeName: "English",
    flag: "🇺🇸",
    preview: false,
    rtl: false,
    loader: () => import("../locales/en/common.json"),
  },
  fr: {
    name: "French",
    nativeName: "Français",
    flag: "🇫🇷",
    preview: false,
    rtl: false,
    loader: () => import("../locales/fr/common.json"),
  },
  rw: {
    name: "Kinyarwanda",
    nativeName: "Kinyarwanda",
    flag: "🇷🇼",
    preview: false,
    rtl: false,
    loader: () => import("../locales/rw/common.json"),
  },
  sw: {
    name: "Swahili",
    nativeName: "Kiswahili",
    flag: "🇰🇪",
    preview: false,
    rtl: false,
    loader: () => import("../locales/sw/common.json"),
  },
  es: {
    name: "Spanish",
    nativeName: "Español",
    flag: "🇪🇸",
    preview: false,
    rtl: false,
    loader: () => import("../locales/es/common.json"),
  },
  am: {
    name: "Amharic",
    nativeName: "አማርኛ",
    flag: "🇪🇹",
    preview: true,
    rtl: false,
    loader: () => import("../locales/am/common.json"),
  },
  ar: {
    name: "Arabic",
    nativeName: "العربية",
    flag: "🇸🇦",
    preview: true,
    rtl: true,
    loader: () => import("../locales/ar/common.json"),
  },
  de: {
    name: "German",
    nativeName: "Deutsch",
    flag: "🇩🇪",
    preview: true,
    rtl: false,
    loader: () => import("../locales/de/common.json"),
  },
  ig: {
    name: "Igbo",
    nativeName: "Igbo",
    flag: "🇳🇬",
    preview: true,
    rtl: false,
    loader: () => import("../locales/ig/common.json"),
  },
  nl: {
    name: "Dutch",
    nativeName: "Nederlands",
    flag: "🇳🇱",
    preview: true,
    rtl: false,
    loader: () => import("../locales/nl/common.json"),
  },
  om: {
    name: "Oromo",
    nativeName: "Afaan Oromoo",
    flag: "🇪🇹",
    preview: true,
    rtl: false,
    loader: () => import("../locales/om/common.json"),
  },
  pt: {
    name: "Portuguese",
    nativeName: "Português",
    flag: "🇵🇹",
    preview: true,
    rtl: false,
    loader: () => import("../locales/pt/common.json"),
  },
  so: {
    name: "Somali",
    nativeName: "Soomaali",
    flag: "🇸🇴",
    preview: true,
    rtl: false,
    loader: () => import("../locales/so/common.json"),
  },
  zh: {
    name: "Chinese",
    nativeName: "中文",
    flag: "🇨🇳",
    preview: true,
    rtl: false,
    loader: () => import("../locales/zh/common.json"),
  },
} as const;

export type LocaleCode = keyof typeof localeCatalog;

type LocaleMeta = (typeof localeCatalog)[LocaleCode];

export const FALLBACK_LOCALE: LocaleCode = "en";

const supportedCodes = Object.keys(localeCatalog) as LocaleCode[];

export const supportedLngs = supportedCodes;

const readyLocaleCodes = supportedCodes.filter((code) =>
  !localeCatalog[code].preview
);
export const READY_LOCALE_CODES = readyLocaleCodes;

const previewLocaleCodes = supportedCodes.filter((code) =>
  localeCatalog[code].preview
);
export const PREVIEW_LOCALE_CODES = previewLocaleCodes;

const resources = Object.fromEntries(
  supportedCodes.map((code) => [code, { common: localeCatalog[code].loader }]),
) as Record<LocaleCode, { common: LocaleMeta["loader"] }>;

const hasWindow = typeof window !== "undefined";
const hasNavigator = typeof navigator !== "undefined";
const hasDocument = typeof document !== "undefined";

let languageChangedHandler: ((lng: string) => void) | null = null;

const safeGetItem = (key: string): string | null => {
  if (!hasWindow) return null;
  try {
    return window.localStorage.getItem(key);
  } catch {
    return null;
  }
};

const safeSetItem = (key: string, value: string) => {
  if (!hasWindow) return;
  try {
    window.localStorage.setItem(key, value);
  } catch {
    /* noop */
  }
};

const isLocaleCode = (locale: string): locale is LocaleCode =>
  Object.hasOwn(localeCatalog, locale);

async function loadBundle(locale: LocaleCode) {
  const loader = resources[locale]?.common ?? resources[FALLBACK_LOCALE].common;
  try {
    const mod = await loader();
    return (mod as { default?: unknown }).default ?? mod;
  } catch {
    const fallbackMod = await resources[FALLBACK_LOCALE].common();
    return (fallbackMod as { default?: unknown }).default ?? fallbackMod;
  }
}

export async function initI18n(startLocale?: LocaleCode) {
  const storedRaw = safeGetItem("locale");
  const stored = storedRaw && isLocaleCode(storedRaw)
    ? (storedRaw as LocaleCode)
    : undefined;
  const browserCandidate = hasNavigator
    ? (navigator.language || "en").slice(0, 2)
    : "en";
  const browser = isLocaleCode(browserCandidate) && !startLocale
    ? (browserCandidate as LocaleCode)
    : undefined;
  const resolved: LocaleCode = startLocale || stored || browser ||
    FALLBACK_LOCALE;

  const bundle = await loadBundle(resolved);

  i18n.use(ICU);
  if (hasWindow) {
    i18n.use(LanguageDetector);
  }
  i18n.use(initReactI18next);

  await i18n.init({
    lng: resolved,
    fallbackLng: FALLBACK_LOCALE,
    supportedLngs: supportedCodes,
    ns: ["common", "translation"],
    defaultNS: "common",
    resources: { [resolved]: { common: bundle } },
    interpolation: { escapeValue: false },
    returnEmptyString: false,
    detection: hasWindow
      ? {
        order: ["localStorage", "querystring", "navigator", "htmlTag"],
        caches: ["localStorage"],
      }
      : undefined,
    react: {
      useSuspense: false,
    },
    // Enable lazy namespace loading
    load: "languageOnly",
    preload: [FALLBACK_LOCALE],
    // Optimize for lazy loading
    saveMissing: false,
    updateMissing: false,
  });

  // Preload fallback so keys always resolve
  if (resolved !== FALLBACK_LOCALE) {
    const fb = await loadBundle(FALLBACK_LOCALE);
    i18n.addResourceBundle(FALLBACK_LOCALE, "common", fb, true, true);
  }

  // Mount legacy translation namespace (keeps existing app keys working)
  i18n.addResourceBundle("en", "translation", legacyEn, true, true);
  i18n.addResourceBundle("es", "translation", legacyEs, true, true);
  i18n.addResourceBundle("fr", "translation", legacyFr, true, true);

  // Reflect current language on the document element
  if (hasDocument) {
    document.documentElement.lang = resolved;
    document.documentElement.dir = localeCatalog[resolved].rtl ? "rtl" : "ltr";
  }

  // Add RTL support, ensuring only one handler is active
  if (languageChangedHandler) {
    i18n.off("languageChanged", languageChangedHandler);
  }
  languageChangedHandler = (lng) => {
    if (!hasDocument) return;
    const normalized = lng.slice(0, 2);
    const next = isLocaleCode(normalized) ? normalized : FALLBACK_LOCALE;
    document.documentElement.lang = next;
    document.documentElement.dir = localeCatalog[next].rtl ? "rtl" : "ltr";
  };
  i18n.on("languageChanged", languageChangedHandler);
}

export async function setLocale(next: LocaleCode) {
  const bundle = await loadBundle(next);
  i18n.addResourceBundle(next, "common", bundle, true, true);
  await i18n.changeLanguage(next);
  safeSetItem("locale", next);
  safeSetItem("i18nextLng", next);
  if (hasDocument) {
    document.documentElement.lang = next;
    document.documentElement.dir = localeCatalog[next].rtl ? "rtl" : "ltr";
  }
}

export async function loadLanguage(lng: string) {
  const code = isLocaleCode(lng) ? lng : FALLBACK_LOCALE;
  await setLocale(code);
}

export { i18n };
export const localeMetadata: Record<LocaleCode, LocaleMeta> = localeCatalog;
export const isReadyLocale = (code: string) =>
  isLocaleCode(code) && !localeCatalog[code].preview;
export default i18n;
